/**
 * Página de Login
 *
 * Tela de autenticação de usuários.
 *
 * @author Equipe VendeAi
 * @since 1.0.0
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff, LogIn, ArrowLeft } from 'lucide-react';
import { useAuth } from '@hooks/useAuth';
import { Alert } from '@components/ui/Alert';
import { Spinner } from '@components/ui/Spinner';
import { logoSquareWhite, logoSquarePrimary } from '@assets/index';

/**
 * Schema de validação do formulário de login
 */
const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .toLowerCase()
    .min(1, 'Email é obrigatório')
    .email('Email inválido'),
  password: z
    .string()
    .min(1, 'Senha é obrigatória')
    .min(8, 'Senha deve ter no mínimo 8 caracteres'),
});

type LoginFormData = z.infer<typeof loginSchema>;

/**
 * Componente de página de Login
 */
export function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();

  // Estado de visibilidade da senha
  const [showPassword, setShowPassword] = useState(false);

  // Estado de loading
  const [isLoading, setIsLoading] = useState(false);

  // Estado de erro da API
  const [apiError, setApiError] = useState<string | null>(null);

  // Sessão encerrada por assinatura/teste expirado (redirecionado pelo interceptor da API)
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // E-mail vindo do cadastro pelo estado da navegação (não vai na URL)
  const emailFromSignup = (location.state as { email?: unknown } | null)?.email;
  const [subscriptionExpired, setSubscriptionExpired] = useState(searchParams.get('expirado') === '1');
  // Vindo do cadastro (#289): conta criada, mas o login automático não completou
  const [justRegistered, setJustRegistered] = useState(searchParams.get('cadastro') === 'ok');

  // Configuração do formulário com React Hook Form (e-mail preenchido quando vem do cadastro)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: typeof emailFromSignup === 'string' ? emailFromSignup : '', password: '' },
  });

  /**
   * Handler de submit do formulário
   */

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setApiError(null);
    setSubscriptionExpired(false);
    setJustRegistered(false);
    try {
      const loggedUser = await login(data);
      // Acesso expirado: dono/gerente vão direto para a contratação do plano
      navigate(loggedUser.subscriptionStatus === 'EXPIRED' ? '/assinatura' : '/painel');
    } catch (error) {
      // Exibe erro no Alert (mensagem da API quando disponível)
      const apiErr = (error as { response?: { data?: { error?: { message?: string; code?: string } } } })
        ?.response?.data?.error;
      if (apiErr?.code === 'SUBSCRIPTION_EXPIRED') {
        setSubscriptionExpired(true);
      }
      const message =
        apiErr?.message ||
        (error instanceof Error ? error.message : 'Erro ao fazer login. Verifique suas credenciais.');
      setApiError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Lado esquerdo - Formulário */}
      <div className="flex w-full flex-col justify-center px-8 md:w-1/2 lg:px-16">
        <div className="mx-auto w-full max-w-md">
          <Link
            to="/"
            className="mb-6 inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <ArrowLeft size={16} /> Voltar ao site
          </Link>

          {/* Logo */}
          <div className="mb-8 flex justify-center">
            <img
              src={logoSquarePrimary}
              alt="VendeAi"
              className="h-20 w-20 rounded-2xl object-contain shadow-lg dark:hidden"
            />
            <img
              src={logoSquareWhite}
              alt="VendeAi"
              className="hidden h-20 w-20 rounded-2xl object-contain shadow-lg dark:block"
            />
          </div>

          {/* Título */}
          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold text-primary-900 dark:text-white">
              Bem-vindo de volta
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Entre com suas credenciais para acessar
            </p>
          </div>

          {/* Formulário */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {justRegistered && (
              <Alert variant="success">
                Sua conta foi criada! Entre com o e-mail e a senha que você cadastrou.
              </Alert>
            )}

            {/* Aviso de período de teste/assinatura encerrado */}
            {subscriptionExpired && (
              <Alert variant="warning">
                Seu período de acesso terminou. O responsável pela loja pode entrar para{' '}
                <strong>contratar um plano</strong> e reativá-la, ou{' '}
                <Link to="/#contato" className="font-semibold underline">
                  fale com nosso time
                </Link>
                .
              </Alert>
            )}

            {/* Alerta de erro */}
            {apiError && !subscriptionExpired && (
              <Alert
                variant="error"
                onClose={() => setApiError(null)}
              >
                {apiError}
              </Alert>
            )}

            {/* Campo Email */}
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Email
              </label>
              <input
                id="email"
                type="email"
                placeholder="seu@email.com"
                {...register('email')}
                className={errors.email ? 'border-error-500' : ''}
              />
              {errors.email && (
                <span className="form-error">{errors.email.message}</span>
              )}
            </div>

            {/* Campo Senha */}
            <div className="form-group">
              <label htmlFor="password" className="form-label">
                Senha
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  {...register('password')}
                  className={errors.password ? 'w-full border-error-500 pr-10' : 'w-full pr-10'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <span className="form-error">{errors.password.message}</span>
              )}
            </div>

            {/* Link esqueci senha */}
            <div className="flex justify-end">
              <Link
                to="/esqueci-senha"
                className="text-sm text-secondary-700 hover:text-secondary-800 dark:text-secondary-400"
              >
                Esqueceu a senha?
              </Link>
            </div>

            {/* Botão de submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="btn btn-primary w-full"
            >
              {isLoading ? (
                <Spinner size="sm" color="white" />
              ) : (
                <>
                  <LogIn size={20} />
                  Entrar
                </>
              )}
            </button>
          </form>

          {/* Link para registro */}
          <p className="mt-8 text-center text-sm text-gray-600 dark:text-gray-400">
            Ainda não tem uma conta?{' '}
            <Link
              to="/cadastro"
              className="font-medium text-secondary-700 hover:text-secondary-800 dark:text-secondary-400"
            >
              Cadastre-se grátis
            </Link>
          </p>
        </div>
      </div>

      {/* Lado direito - Ilustração */}
      <div className="hidden bg-gradient-brand md:flex md:w-1/2 md:flex-col md:items-center md:justify-center md:p-12">
        <div className="text-center text-white">
          <h2 className="text-3xl font-bold text-white">VendeAi</h2>
          <p className="mt-4 max-w-md text-lg opacity-90">
            Sistema completo para gerenciamento de pedidos e delivery.
            Automatize suas vendas e aumente seu faturamento.
          </p>
        </div>
      </div>
    </div>
  );
}
